import { createContext, useContext, useState, ReactNode, useEffect, useRef } from "react";
import { logout as apiLogout } from "@/api/auth";
import { safeDecodeToken, getTimeUntilExpiration, isTokenExpired } from "@/utils/jwt";

type AuthContextType = {
  isAuthenticated: boolean;
  userRole: Array<string> | undefined;
  userId: string | null;
  displayName: string | null;
  currentView: 'main' | 'expert' | 'projects' | 'sales' | 'timeEntries' | 'projectReferences' | 'approval' | 'customers' | 'priceLists' | 'invoices' | 'projectManagement' | 'salary';
  defaultTab: 'skills' | 'project-history';
  setDefaultTab: (tab: 'skills' | 'project-history') => void;
  setCurrentView: (view: 'main' | 'expert' | 'projects' | 'sales' | 'timeEntries' | 'projectReferences' | 'approval' | 'customers' | 'priceLists' | 'invoices' | 'projectManagement' | 'salary') => void;
  logout: (token: string) => Promise<void>;
  setIsAuthenticated: (value: boolean) => void;
  setUserRole: (role: Array<string> | undefined) => void;
  setDisplayName: (displayName: string | null) => void;
  sessionId?: string;
  setSessionId?: (sessionId: string) => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const token = localStorage.getItem("accessToken");
    return !!token;
  });

  const [userId] = useState<string | null>(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      return null;
    }
    const decoded = safeDecodeToken(token);
    return decoded?.id || null;
  })

  const [userRole, setUserRole] = useState<Array<string> | undefined>(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      return undefined;
    }
    const decoded = safeDecodeToken(token);
    return decoded?.roles;
  });

  const [displayName, setDisplayName] = useState<string | null>(() => {
    const displayName = localStorage.getItem("displayName");
    return displayName;
  });

  const [currentView, setCurrentView] = useState<'main' | 'expert' | 'projects' | 'sales' | 'timeEntries' | 'projectReferences' | 'approval' | 'customers' | 'priceLists' | 'invoices' | 'projectManagement' | 'salary'>('timeEntries');
  const [defaultTab, setDefaultTab] = useState<'skills' | 'project-history'>('skills');
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);

  // Reference to store timeout ID for session monitoring
  const sessionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const logout = async (token: string) => {
    try {
      const decoded = safeDecodeToken(token);
      // Call the API to invalidate the token if user is logged in
      if (decoded?.email) {
        await apiLogout(decoded.email);
      }
    } catch (error) {
      console.error("Error during logout:", error);
    } finally {
      // Clean up local storage and state regardless of API success
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("userRole");
      localStorage.removeItem("displayName");
      setUserRole(undefined);
      setIsAuthenticated(false);
      // Reset view to default when logging out
      setCurrentView('main');
      
      // Clear session timeout
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
        sessionTimeoutRef.current = null;
      }
    }
  };

  // Monitor session timeout - automatically logout when refresh token expires (24 hours)
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      return;
    }

    // Check refresh token expiration and set up automatic logout
    const timeUntilExpiration = getTimeUntilExpiration(refreshToken);
    
    if (timeUntilExpiration <= 0) {
      // Refresh token is already expired, logout immediately
      console.log('Refresh token expired, logging out');
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        logout(accessToken);
      }
      return;
    }

    // Set timeout to logout when refresh token expires
    sessionTimeoutRef.current = setTimeout(async () => {
      console.log('Session timeout - refresh token expired (24 hours), logging out');
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        await logout(accessToken);
      }
      // Redirect to login page, avoiding an unnecessary reload if already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }, timeUntilExpiration);

    // Cleanup on unmount
    return () => {
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
      }
    };
  }, [isAuthenticated]);

  // Check if tokens are expired on mount and periodically
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const checkTokens = async () => {
      const accessToken = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');

      // Check if refresh token is expired
      if (refreshToken && isTokenExpired(refreshToken)) {
        console.log('Refresh token expired during periodic check');
        if (accessToken) {
          // Wait for logout to finish clearing localStorage before redirecting,
          // otherwise a page reload can race ahead of the cleanup and re-trigger
          // this same check on mount, causing an infinite reload loop.
          await logout(accessToken);
        }
        // Avoid redirecting (and reloading) if we're already on the login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    };

    // Check immediately
    checkTokens();

    // Check every 5 minutes
    const intervalId = setInterval(checkTokens, 5 * 60 * 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [isAuthenticated]);

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      userRole,
      displayName,
      currentView,
      defaultTab,
      setDefaultTab,
      setCurrentView,
      logout,
      setIsAuthenticated,
      setUserRole,
      setDisplayName,
      userId,
      sessionId,
      setSessionId
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}